import * as React from 'react'
import { cn } from '../../utils/cn'
import { FieldLabelContent } from './field-label'

/**
 * JPG Academy Date Picker & DateRangePicker
 *
 * Calendar-based date selection matching Input styling.
 * Gold focus ring, warm borders, cream-tinted calendar grid.
 *
 * Features:
 * - Single date or date range selection
 * - Navigate months/years
 * - Highlight today, selected dates, range
 * - "Today" quick-select button
 * - Disabled dates support
 * - Week starts on Monday
 * - Keyboard navigation
 * - Accessible: role="grid", aria-label
 *
 * Usage:
 *   <DatePicker
 *     label="Start Date"
 *     value={startDate}
 *     onChange={setStartDate}
 *   />
 *
 *   <DateRangePicker
 *     label="Date Range"
 *     start={rangeStart}
 *     end={rangeEnd}
 *     onChange={({ start, end }) => setRange({ start, end })}
 *   />
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
]

function sameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
}

function isBetween(date: Date, start: Date, end: Date): boolean {
	const t = date.getTime()
	return t > start.getTime() && t < end.getTime()
}

function addMonths(date: Date, months: number): Date {
	const d = new Date(date)
	d.setMonth(d.getMonth() + months)
	return d
}

function getCalendarDays(year: number, month: number): (Date | null)[] {
	const firstDay = new Date(year, month, 1)
	const lastDay = new Date(year, month + 1, 0)

	// Monday = 0, Sunday = 6
	let startDow = firstDay.getDay() - 1
	if (startDow < 0) startDow = 6

	const days: (Date | null)[] = []
	// Padding before first day
	for (let i = 0; i < startDow; i++) days.push(null)
	// Actual days
	for (let d = 1; d <= lastDay.getDate(); d++) {
		days.push(new Date(year, month, d))
	}
	// Padding after last day
	while (days.length % 7 !== 0) days.push(null)

	return days
}

function formatDate(date: Date): string {
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Calendar Grid (shared) ─────────────────────────────────────────────────

interface CalendarGridProps {
	month: Date
	selected?: Date | null
	rangeStart?: Date | null
	rangeEnd?: Date | null
	hoverDate?: Date | null
	disabledDate?: (date: Date) => boolean
	onSelect?: (date: Date) => void
	onHover?: (date: Date | null) => void
	today?: Date
}

function CalendarGrid({
	month,
	selected,
	rangeStart,
	rangeEnd,
	hoverDate,
	disabledDate,
	onSelect,
	onHover,
	today = new Date(),
}: CalendarGridProps) {
	const year = month.getFullYear()
	const m = month.getMonth()
	const days = getCalendarDays(year, m)

	return (
		<div>
			{/* Day headers */}
			<div className="grid grid-cols-7 mb-1">
				{DAYS.map(day => (
					<div
						key={day}
						className="text-center text-[10px] font-ui font-semibold text-muted-foreground uppercase tracking-wider py-1"
					>
						{day}
					</div>
				))}
			</div>

			{/* Day cells */}
			<div className="grid grid-cols-7" role="grid" aria-label={`${MONTHS[m]} ${year}`}>
				{days.map((day, i) => {
					if (!day) {
						return <div key={`empty-${i}`} className="h-8" />
					}

					const isToday = sameDay(day, today)
					const isSelected = selected ? sameDay(day, selected) : false
					const isRangeStart = rangeStart ? sameDay(day, rangeStart) : false
					const isRangeEnd = rangeEnd ? sameDay(day, rangeEnd) : false
					const isInRange = rangeStart && rangeEnd
						? (isBetween(day, rangeStart, rangeEnd) || isRangeStart || isRangeEnd)
						: false
					const isHovered = hoverDate && rangeStart && !rangeEnd
						? (isBetween(day, rangeStart, hoverDate) || isBetween(day, hoverDate, rangeStart))
						: false
					const isDisabled = disabledDate?.(day) ?? false

					return (
						<button
							key={day.toISOString()}
							type="button"
							disabled={isDisabled}
							role="gridcell"
							aria-selected={isSelected || isRangeStart || isRangeEnd}
							aria-label={formatDate(day)}
							onClick={() => !isDisabled && onSelect?.(day)}
							onMouseEnter={() => onHover?.(day)}
							onMouseLeave={() => onHover?.(null)}
							className={cn(
								'h-8 w-full text-sm font-ui relative transition-colors duration-fast',
								// Range background
								(isInRange || isHovered) && !isDisabled && 'bg-gold/10',
								// Today ring
								isToday && !isSelected && !isRangeStart && !isRangeEnd &&
									'font-semibold text-navy-900',
								// Selected
								(isSelected || isRangeStart || isRangeEnd) &&
									'bg-navy text-cream-50 font-semibold rounded-full',
								// Hover on non-selected
								!isSelected && !isRangeStart && !isRangeEnd && !isDisabled &&
									'hover:bg-cream/60',
								// Disabled
								isDisabled && 'text-muted-foreground/30 cursor-not-allowed',
								// Default
								!isSelected && !isRangeStart && !isRangeEnd && !isDisabled &&
									'text-foreground',
							)}
						>
							{day.getDate()}
							{/* Today indicator */}
							{isToday && !isSelected && !isRangeStart && !isRangeEnd && (
								<span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gold" />
							)}
						</button>
					)
				})}
			</div>
		</div>
	)
}

// ─── Chevron Icons ──────────────────────────────────────────────────────────

function ChevronLeft({ className }: { className?: string }) {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
			<path d="m15 18-6-6 6-6" />
		</svg>
	)
}

function ChevronRight({ className }: { className?: string }) {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
			<path d="m9 18 6-6-6-6" />
		</svg>
	)
}

// ─── Month Navigator ────────────────────────────────────────────────────────

interface MonthNavProps {
	month: Date
	onChange: (month: Date) => void
}

function MonthNav({ month, onChange }: MonthNavProps) {
	return (
		<div className="flex items-center justify-between mb-3">
			<button
				type="button"
				onClick={() => onChange(addMonths(month, -1))}
				className="p-1.5 rounded-md hover:bg-cream/60 transition-colors text-muted-foreground hover:text-foreground"
				aria-label="Previous month"
			>
				<ChevronLeft />
			</button>
			<span className="font-display text-sm uppercase tracking-trajan font-bold text-foreground">
				{MONTHS[month.getMonth()]} {month.getFullYear()}
			</span>
			<button
				type="button"
				onClick={() => onChange(addMonths(month, 1))}
				className="p-1.5 rounded-md hover:bg-cream/60 transition-colors text-muted-foreground hover:text-foreground"
				aria-label="Next month"
			>
				<ChevronRight />
			</button>
		</div>
	)
}

// ─── DatePicker ─────────────────────────────────────────────────────────────

export interface DatePickerProps {
	/** Selected date */
	value?: Date | null
	/** Change handler */
	onChange?: (date: Date) => void
	/** Label text */
	label?: string
	/** Contextual help shown beside the label */
	labelHelp?: React.ReactNode
	/** Optional title for the label help tooltip */
	labelHelpTitle?: string
	/** Placeholder text */
	placeholder?: string
	/** Disable specific dates */
	disabledDate?: (date: Date) => boolean
	/** Additional class */
	className?: string
}

export function DatePicker({
	value,
	onChange,
	label,
	labelHelp,
	labelHelpTitle,
	placeholder = 'Select date...',
	disabledDate,
	className,
}: DatePickerProps) {
	const [month, setMonth] = React.useState(value ?? new Date())
	const [isOpen, setIsOpen] = React.useState(false)
	const containerRef = React.useRef<HTMLDivElement>(null)

	// Sync month when value changes externally
	React.useEffect(() => {
		if (value) setMonth(value)
	}, [value])

	// Close on outside click
	React.useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	const today = new Date()
	const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())

	return (
		<div className={cn('space-y-1.5', className)} ref={containerRef}>
			{label && (
				<label className="block text-sm font-medium text-foreground font-ui">
					<FieldLabelContent label={label} labelHelp={labelHelp} labelHelpTitle={labelHelpTitle} />
				</label>
			)}

			{/* Trigger */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={cn(
					'w-full h-10 px-3 text-sm font-ui rounded-md border text-left',
					'flex items-center justify-between gap-2',
					'bg-background text-foreground',
					'placeholder:text-muted-foreground',
					'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
					'transition-all duration-fast',
					isOpen ? 'border-gold' : 'border-input hover:border-navy-300',
				)}
			>
				<span className={cn(!value && 'text-muted-foreground')}>
					{value ? formatDate(value) : placeholder}
				</span>
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
					<rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
					<path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
				</svg>
			</button>

			{/* Calendar popover */}
			{isOpen && (
				<div
					className={cn(
						'absolute z-dropdown mt-1 p-4',
						'bg-card border border-border rounded-xl',
						'shadow-lg animate-scale-in',
						'w-[300px]',
					)}
				>
					<MonthNav month={month} onChange={setMonth} />
					<CalendarGrid
						month={month}
						selected={value ?? null}
						onSelect={(date) => {
							onChange?.(date)
							setIsOpen(false)
						}}
						today={todayDate}
					/>
					<button
						type="button"
						onClick={() => {
							onChange?.(todayDate)
							setIsOpen(false)
						}}
						className="mt-3 w-full text-xs font-ui text-navy-700 hover:text-navy-900 font-medium py-1.5 rounded-md hover:bg-cream/60 transition-colors text-center"
					>
						Today — {formatDate(todayDate)}
					</button>
				</div>
			)}
		</div>
	)
}

// ─── DateRangePicker ────────────────────────────────────────────────────────

export interface DateRange {
	start: Date | null
	end: Date | null
}

export interface DateRangePickerProps {
	/** Selected range */
	value?: DateRange
	/** Change handler */
	onChange?: (range: DateRange) => void
	/** Label text */
	label?: string
	/** Contextual help shown beside the label */
	labelHelp?: React.ReactNode
	/** Optional title for the label help tooltip */
	labelHelpTitle?: string
	/** Placeholder text */
	placeholder?: string
	/** Disable specific dates */
	disabledDate?: (date: Date) => boolean
	/** Additional class */
	className?: string
}

export function DateRangePicker({
	value = { start: null, end: null },
	onChange,
	label,
	labelHelp,
	labelHelpTitle,
	placeholder = 'Select date range...',
	disabledDate,
	className,
}: DateRangePickerProps) {
	const [month, setMonth] = React.useState(value.start ?? new Date())
	const [isOpen, setIsOpen] = React.useState(false)
	const [hoverDate, setHoverDate] = React.useState<Date | null>(null)
	const containerRef = React.useRef<HTMLDivElement>(null)

	const nextMonth = addMonths(month, 1)

	// Close on outside click
	React.useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	const today = new Date()
	const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())

	const handleSelect = (date: Date) => {
		if (!value.start || (value.start && value.end)) {
			// Start new range
			onChange?.({ start: date, end: null })
		} else {
			// Complete range
			if (date < value.start) {
				onChange?.({ start: date, end: value.start })
			} else {
				onChange?.({ start: value.start, end: date })
			}
		}
	}

	/** Navigate both calendars back/forward by one month */
	const handlePrevMonth = () => setMonth((m) => addMonths(m, -1))
	const handleNextMonth = () => setMonth((m) => addMonths(m, 1))

	const displayValue = value.start && value.end
		? `${formatDate(value.start)} – ${formatDate(value.end)}`
		: value.start
			? `${formatDate(value.start)} – ...`
			: ''

	return (
		<div className={cn('space-y-1.5', className)} ref={containerRef}>
			{label && (
				<label className="block text-sm font-medium text-foreground font-ui">
					<FieldLabelContent label={label} labelHelp={labelHelp} labelHelpTitle={labelHelpTitle} />
				</label>
			)}

			{/* Trigger */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={cn(
					'w-full h-10 px-3 text-sm font-ui rounded-md border text-left',
					'flex items-center justify-between gap-2',
					'bg-background text-foreground',
					'placeholder:text-muted-foreground',
					'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
					'transition-all duration-fast',
					isOpen ? 'border-gold' : 'border-input hover:border-navy-300',
				)}
			>
				<span className={cn(!displayValue && 'text-muted-foreground')}>
					{displayValue || placeholder}
				</span>
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
					<rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
					<path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
				</svg>
			</button>

			{/* Dual-calendar popover */}
			{isOpen && (
				<div
					className={cn(
						'absolute z-dropdown mt-1 p-4',
						'bg-card border border-border rounded-xl',
						'shadow-lg animate-scale-in',
						'w-[620px]',
					)}
				>
					{/* Navigation header spanning both calendars */}
					<div className="flex items-center justify-between mb-3">
						<button
							type="button"
							onClick={handlePrevMonth}
							className="p-1.5 rounded-md hover:bg-cream/60 transition-colors text-muted-foreground hover:text-foreground"
							aria-label="Previous month"
						>
							<ChevronLeft />
						</button>
						<div className="flex items-center gap-4">
							<span className="font-display text-sm uppercase tracking-trajan font-bold text-foreground">
								{MONTHS[month.getMonth()]} {month.getFullYear()}
							</span>
							<span className="text-muted-foreground/40">–</span>
							<span className="font-display text-sm uppercase tracking-trajan font-bold text-foreground">
								{MONTHS[nextMonth.getMonth()]} {nextMonth.getFullYear()}
							</span>
						</div>
						<button
							type="button"
							onClick={handleNextMonth}
							className="p-1.5 rounded-md hover:bg-cream/60 transition-colors text-muted-foreground hover:text-foreground"
							aria-label="Next month"
						>
							<ChevronRight />
						</button>
					</div>

					{/* Two calendars side by side */}
					<div className="grid grid-cols-2 gap-6">
						<CalendarGrid
							month={month}
							rangeStart={value.start}
							rangeEnd={value.end}
							hoverDate={hoverDate}
							onSelect={handleSelect}
							onHover={setHoverDate}
							today={todayDate}
						/>
						<CalendarGrid
							month={nextMonth}
							rangeStart={value.start}
							rangeEnd={value.end}
							hoverDate={hoverDate}
							onSelect={handleSelect}
							onHover={setHoverDate}
							today={todayDate}
						/>
					</div>

					<div className="flex gap-2 mt-3">
						<button
							type="button"
							onClick={() => {
								onChange?.({ start: todayDate, end: todayDate })
							}}
							className="flex-1 text-xs font-ui text-navy-700 hover:text-navy-900 font-medium py-1.5 rounded-md hover:bg-cream/60 transition-colors text-center"
						>
							Today
						</button>
						<button
							type="button"
							onClick={() => {
								onChange?.({ start: null, end: null })
							}}
							className="flex-1 text-xs font-ui text-muted-foreground hover:text-foreground font-medium py-1.5 rounded-md hover:bg-cream/60 transition-colors text-center"
						>
							Clear
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
